"use strict";
/**
 * Copyright 2017 Google Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 * http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
var __extends = (this && this.__extends) || (function () {
 var extendStatics = Object.setPrototypeOf ||
 ({ __proto__: [] } instanceof Array && function (d, b) { d.__proto__ = b; }) ||
 function (d, b) { for (var p in b) if (b.hasOwnProperty(p)) d[p] = b[p]; };
 return function (d, b) {
 extendStatics(d, b);
 function __() { this.constructor = d; }
 d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
 };
})();
Object.defineProperty(exports, "__esModule", { value: true });
var util_1 = require("@firebase/util");
var util_2 = require("../util/util");
var SortedMap_1 = require("../util/SortedMap");
var Node_1 = require("./Node");
var snap_1 = require("./snap");
var PriorityIndex_1 = require("./indexes/PriorityIndex");
var KeyIndex_1 = require("./indexes/KeyIndex");
var IndexMap_1 = require("./IndexMap");
var LeafNode_1 = require("./LeafNode");
var comparators_1 = require("./comparators");
// TODO: For memory savings, don't store priorityNode_ if it's empty.
var EMPTY_NODE;
/**
 * ChildrenNode is a class for storing internal nodes in a DataSnapshot
 * (i.e. nodes with children). It implements Node and stores the
 * list of children in the children property, sorted by child name.
 *
 * @constructor
 * @implements {Node}
 */
var ChildrenNode = /** @class */ (function () {
 /**
 *
 * @param {!SortedMap.} children_ List of children
 * of this node..
 * @param {?Node} priorityNode_ The priority of this node (as a snapshot node).
 * @param {!IndexMap} indexMap_
 */
 function ChildrenNode(children_, priorityNode_, indexMap_) {
 this.children_ = children_;
 this.priorityNode_ = priorityNode_;
 this.indexMap_ = indexMap_;
 this.lazyHash_ = null;
 /**
 * Note: The only reason we allow null priority is for EMPTY_NODE, since we can't use
 * EMPTY_NODE as the priority of EMPTY_NODE. We might want to consider making EMPTY_NODE its own
 * class instead of an empty ChildrenNode.
 */
 if (this.priorityNode_) {
 snap_1.validatePriorityNode(this.priorityNode_);
 }
 if (this.children_.isEmpty()) {
 util_1.assert(!this.priorityNode_ || this.priorityNode_.isEmpty(), 'An empty node cannot have a priority');
 }
 }
 Object.defineProperty(ChildrenNode, "EMPTY_NODE", {
 get: function () {
 return (EMPTY_NODE ||
 (EMPTY_NODE = new ChildrenNode(new SortedMap_1.SortedMap(comparators_1.NAME_COMPARATOR), null, IndexMap_1.IndexMap.Default)));
 },
 enumerable: true,
 configurable: true
 });
 /** @inheritDoc */
 ChildrenNode.prototype.isLeafNode = function () {
 return false;
 };
 /** @inheritDoc */
 ChildrenNode.prototype.getPriority = function () {
 return this.priorityNode_ || EMPTY_NODE;
 };
 /** @inheritDoc */
 ChildrenNode.prototype.updatePriority = function (newPriorityNode) {
 if (this.children_.isEmpty()) {
 // Don't allow priorities on empty nodes
 return this;
 }
 else {
 return new ChildrenNode(this.children_, newPriorityNode, this.indexMap_);
 }
 };
 /** @inheritDoc */
 ChildrenNode.prototype.getImmediateChild = function (childName) {
 // Hack to treat priority as a regular child
 if (childName === '.priority') {
 return this.getPriority();
 }
 else {
 var child = this.children_.get(childName);
 return child === null ? EMPTY_NODE : child;
 }
 };
 /** @inheritDoc */
 ChildrenNode.prototype.getChild = function (path) {
 var front = path.getFront();
 if (front === null)
 return this;
 return this.getImmediateChild(front).getChild(path.popFront());
 };
 /** @inheritDoc */
 ChildrenNode.prototype.hasChild = function (childName) {
 return this.children_.get(childName) !== null;
 };
 /** @inheritDoc */
 ChildrenNode.prototype.updateImmediateChild = function (childName, newChildNode) {
 util_1.assert(newChildNode, 'We should always be passing snapshot nodes');
 if (childName === '.priority') {
 return this.updatePriority(newChildNode);
 }
 else {
 var namedNode = new Node_1.NamedNode(childName, newChildNode);
 var newChildren = void 0, newIndexMap = void 0, newPriority = void 0;
 if (newChildNode.isEmpty()) {
 newChildren = this.children_.remove(childName);
 newIndexMap = this.indexMap_.removeFromIndexes(namedNode, this.children_);
 }
 else {
 newChildren = this.children_.insert(childName, newChildNode);
 newIndexMap = this.indexMap_.addToIndexes(namedNode, this.children_);
 }
 newPriority = newChildren.isEmpty() ? EMPTY_NODE : this.priorityNode_;
 return new ChildrenNode(newChildren, newPriority, newIndexMap);
 }
 };
 /** @inheritDoc */
 ChildrenNode.prototype.updateChild = function (path, newChildNode) {
 var front = path.getFront();
 if (front === null) {
 return newChildNode;
 }
 else {
 util_1.assert(path.getFront() !== '.priority' || path.getLength() === 1, '.priority must be the last token in a path');
 var newImmediateChild = this.getImmediateChild(front).updateChild(path.popFront(), newChildNode);
 return this.updateImmediateChild(front, newImmediateChild);
 }
 };
 /** @inheritDoc */
 ChildrenNode.prototype.isEmpty = function () {
 return this.children_.isEmpty();
 };
 /** @inheritDoc */
 ChildrenNode.prototype.numChildren = function () {
 return this.children_.count();
 };
 /** @inheritDoc */
 ChildrenNode.prototype.val = function (exportFormat) {
 if (this.isEmpty())
 return null;
 var obj = {};
 var numKeys = 0, maxKey = 0, allIntegerKeys = true;
 this.forEachChild(PriorityIndex_1.PRIORITY_INDEX, function (key, childNode) {
 obj[key] = childNode.val(exportFormat);
 numKeys++;
 if (allIntegerKeys && ChildrenNode.INTEGER_REGEXP_.test(key)) {
 maxKey = Math.max(maxKey, Number(key));
 }
 else {
 allIntegerKeys = false;
 }
 });
 if (!exportFormat && allIntegerKeys && maxKey < 2 * numKeys) {
 // convert to array.
 var array = [];
 for (var key in obj)
 array[key] = obj[key];
 return array;
 }
 else {
 if (exportFormat && !this.getPriority().isEmpty()) {
 obj['.priority'] = this.getPriority().val();
 }
 return obj;
 }
 };
 /** @inheritDoc */
 ChildrenNode.prototype.hash = function () {
 if (this.lazyHash_ === null) {
 var toHash_1 = '';
 if (!this.getPriority().isEmpty())
 toHash_1 +=
 'priority:' +
 snap_1.priorityHashText(this.getPriority().val()) +
 ':';
 this.forEachChild(PriorityIndex_1.PRIORITY_INDEX, function (key, childNode) {
 var childHash = childNode.hash();
 if (childHash !== '')
 toHash_1 += ':' + key + ':' + childHash;
 });
 this.lazyHash_ = toHash_1 === '' ? '' : util_2.sha1(toHash_1);
 }
 return this.lazyHash_;
 };
 /** @inheritDoc */
 ChildrenNode.prototype.getPredecessorChildName = function (childName, childNode, index) {
 var idx = this.resolveIndex_(index);
 if (idx) {
 var predecessor = idx.getPredecessorKey(new Node_1.NamedNode(childName, childNode));
 return predecessor ? predecessor.name : null;
 }
 else {
 return this.children_.getPredecessorKey(childName);
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {?string}
 */
 ChildrenNode.prototype.getFirstChildName = function (indexDefinition) {
 var idx = this.resolveIndex_(indexDefinition);
 if (idx) {
 var minKey = idx.minKey();
 return minKey && minKey.name;
 }
 else {
 return this.children_.minKey();
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {?NamedNode}
 */
 ChildrenNode.prototype.getFirstChild = function (indexDefinition) {
 var minKey = this.getFirstChildName(indexDefinition);
 if (minKey) {
 return new Node_1.NamedNode(minKey, this.children_.get(minKey));
 }
 else {
 return null;
 }
 };
 /**
 * Given an index, return the key name of the largest value we have, according to that index
 * @param {!Index} indexDefinition
 * @return {?string}
 */
 ChildrenNode.prototype.getLastChildName = function (indexDefinition) {
 var idx = this.resolveIndex_(indexDefinition);
 if (idx) {
 var maxKey = idx.maxKey();
 return maxKey && maxKey.name;
 }
 else {
 return this.children_.maxKey();
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {?NamedNode}
 */
 ChildrenNode.prototype.getLastChild = function (indexDefinition) {
 var maxKey = this.getLastChildName(indexDefinition);
 if (maxKey) {
 return new Node_1.NamedNode(maxKey, this.children_.get(maxKey));
 }
 else {
 return null;
 }
 };
 /**
 * @inheritDoc
 */
 ChildrenNode.prototype.forEachChild = function (index, action) {
 var idx = this.resolveIndex_(index);
 if (idx) {
 return idx.inorderTraversal(function (wrappedNode) {
 return action(wrappedNode.name, wrappedNode.node);
 });
 }
 else {
 return this.children_.inorderTraversal(action);
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {SortedMapIterator}
 */
 ChildrenNode.prototype.getIterator = function (indexDefinition) {
 return this.getIteratorFrom(indexDefinition.minPost(), indexDefinition);
 };
 /**
 *
 * @param {!NamedNode} startPost
 * @param {!Index} indexDefinition
 * @return {!SortedMapIterator}
 */
 ChildrenNode.prototype.getIteratorFrom = function (startPost, indexDefinition) {
 var idx = this.resolveIndex_(indexDefinition);
 if (idx) {
 return idx.getIteratorFrom(startPost, function (key) { return key; });
 }
 else {
 var iterator = this.children_.getIteratorFrom(startPost.name, Node_1.NamedNode.Wrap);
 var next = iterator.peek();
 while (next != null && indexDefinition.compare(next, startPost) < 0) {
 iterator.getNext();
 next = iterator.peek();
 }
 return iterator;
 }
 };
 /**
 * @param {!Index} indexDefinition
 * @return {!SortedMapIterator}
 */
 ChildrenNode.prototype.getReverseIterator = function (indexDefinition) {
 return this.getReverseIteratorFrom(indexDefinition.maxPost(), indexDefinition);
 };
 /**
 * @param {!NamedNode} endPost
 * @param {!Index} indexDefinition
 * @return {!SortedMapIterator}
 */
 ChildrenNode.prototype.getReverseIteratorFrom = function (endPost, indexDefinition) {
 var idx = this.resolveIndex_(indexDefinition);
 if (idx) {
 return idx.getReverseIteratorFrom(endPost, function (key) {
 return key;
 });
 }
 else {
 var iterator = this.children_.getReverseIteratorFrom(endPost.name, Node_1.NamedNode.Wrap);
 var next = iterator.peek();
 while (next != null && indexDefinition.compare(next, endPost) > 0) {
 iterator.getNext();
 next = iterator.peek();
 }
 return iterator;
 }
 };
 /**
 * @inheritDoc
 */
 ChildrenNode.prototype.compareTo = function (other) {
 if (this.isEmpty()) {
 if (other.isEmpty()) {
 return 0;
 }
 else {
 return -1;
 }
 }
 else if (other.isLeafNode() || other.isEmpty()) {
 return 1;
 }
 else if (other === exports.MAX_NODE) {
 return -1;
 }
 else {
 // Must be another node with children.
 return 0;
 }
 };
 /**
 * @inheritDoc
 */
 ChildrenNode.prototype.withIndex = function (indexDefinition) {
 if (indexDefinition === KeyIndex_1.KEY_INDEX ||
 this.indexMap_.hasIndex(indexDefinition)) {
 return this;
 }
 else {
 var newIndexMap = this.indexMap_.addIndex(indexDefinition, this.children_);
 return new ChildrenNode(this.children_, this.priorityNode_, newIndexMap);
 }
 };
 /**
 * @inheritDoc
 */
 ChildrenNode.prototype.isIndexed = function (index) {
 return index === KeyIndex_1.KEY_INDEX || this.indexMap_.hasIndex(index);
 };
 /**
 * @inheritDoc
 */
 ChildrenNode.prototype.equals = function (other) {
 if (other === this) {
 return true;
 }
 else if (other.isLeafNode()) {
 return false;
 }
 else {
 var otherChildrenNode = other;
 if (!this.getPriority().equals(otherChildrenNode.getPriority())) {
 return false;
 }
 else if (this.children_.count() === otherChildrenNode.children_.count()) {
 var thisIter = this.getIterator(PriorityIndex_1.PRIORITY_INDEX);
 var otherIter = otherChildrenNode.getIterator(PriorityIndex_1.PRIORITY_INDEX);
 var thisCurrent = thisIter.getNext();
 var otherCurrent = otherIter.getNext();
 while (thisCurrent && otherCurrent) {
 if (thisCurrent.name !== otherCurrent.name ||
 !thisCurrent.node.equals(otherCurrent.node)) {
 return false;
 }
 thisCurrent = thisIter.getNext();
 otherCurrent = otherIter.getNext();
 }
 return thisCurrent === null && otherCurrent === null;
 }
 else {
 return false;
 }
 }
 };
 /**
 * Returns a SortedMap ordered by index, or null if the default (by-key) ordering can be used
 * instead.
 *
 * @private
 * @param {!Index} indexDefinition
 * @return {?SortedMap.}
 */
 ChildrenNode.prototype.resolveIndex_ = function (indexDefinition) {
 if (indexDefinition === KeyIndex_1.KEY_INDEX) {
 return null;
 }
 else {
 return this.indexMap_.get(indexDefinition.toString());
 }
 };
 /**
 * @private
 * @type {RegExp}
 */
 ChildrenNode.INTEGER_REGEXP_ = /^(0|[1-9]\d*)$/;
 return ChildrenNode;
}());
exports.ChildrenNode = ChildrenNode;
/**
 * @constructor
 * @extends {ChildrenNode}
 * @private
 */
var MaxNode = /** @class */ (function (_super) {
 __extends(MaxNode, _super);
 function MaxNode() {
 return _super.call(this, new SortedMap_1.SortedMap(comparators_1.NAME_COMPARATOR), ChildrenNode.EMPTY_NODE, IndexMap_1.IndexMap.Default) || this;
 }
 MaxNode.prototype.compareTo = function (other) {
 if (other === this) {
 return 0;
 }
 else {
 return 1;
 }
 };
 MaxNode.prototype.equals = function (other) {
 // Not that we every compare it, but MAX_NODE is only ever equal to itself
 return other === this;
 };
 MaxNode.prototype.getPriority = function () {
 return this;
 };
 MaxNode.prototype.getImmediateChild = function (childName) {
 return ChildrenNode.EMPTY_NODE;
 };
 MaxNode.prototype.isEmpty = function () {
 return false;
 };
 return MaxNode;
}(ChildrenNode));
exports.MaxNode = MaxNode;
/**
 * Marker that will sort higher than any other snapshot.
 * @type {!MAX_NODE}
 * @const
 */
exports.MAX_NODE = new MaxNode();
Object.defineProperties(Node_1.NamedNode, {
 MIN: {
 value: new Node_1.NamedNode(util_2.MIN_NAME, ChildrenNode.EMPTY_NODE)
 },
 MAX: {
 value: new Node_1.NamedNode(util_2.MAX_NAME, exports.MAX_NODE)
 }
});
/**
 * Reference Extensions
 */
KeyIndex_1.KeyIndex.__EMPTY_NODE = ChildrenNode.EMPTY_NODE;
LeafNode_1.LeafNode.__childrenNodeConstructor = ChildrenNode;
snap_1.setMaxNode(exports.MAX_NODE);
PriorityIndex_1.setMaxNode(exports.MAX_NODE);

//# sourceMappingURL=ChildrenNode.js.map



//////////////////
// WEBPACK FOOTER
// ../database/dist/cjs/src/core/snap/ChildrenNode.js
// module id = 4
// module chunks = 0